import {
  createContext,
  type ReactNode,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useOktaAuth } from '@okta/okta-react/';
import {
  type AccessToken,
  type UserClaims,
  decodeToken,
} from '@okta/okta-auth-js';
import type { UserInterface, UserContextType, perms } from '../@types/user';
import { init } from '@amplitude/analytics-browser';
import { useLocationStore } from '../stores/locationStore';
import { getMobileOS } from '../utils/platform/getMobileOS';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { setUserAvatar } from './setUserAvatar';
import {
  categoricalNetworkInfoEquals,
  type CategoricalNetworkInfo,
  getCategoricalNetworkInfo,
  setAmplitudeNetworkProps,
  setAmplitudeUserProps,
} from './setAmplitudeUserProps';
import { subscribeToNetworkChange } from '../utils/platform/getNetworkInfo';
import { v4 as uuidv4 } from 'uuid';
import { getEsrGraphqlHostForSubscription } from '../utils/getEsrSubscriptionConnection';
import { getAmplitudeToken } from '../utils/getAmplitudeToken';
import { getViteAppUrl } from '../utils/Vite/getViteAppUrl';

// okta-auth-js v8 only populates accessToken.claims when decodeAccessTokens
// is enabled, so fall back to decoding the raw JWT.
function getAccessTokenClaims(accessToken: AccessToken): Partial<UserClaims> {
  if (accessToken.claims) return accessToken.claims;
  try {
    return decodeToken(accessToken.accessToken).payload as UserClaims;
  } catch {
    return {};
  }
}

export const UserContext = createContext<Nullable<UserContextType>>(null);

const UserProvider = ({ children }: { children: ReactNode }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const ldClient = useLDClient();
  const flags = useFlags();
  const [ldIdentified, setLdIdentified] = useState<boolean>(false);
  const [userState, setUserState] = useState<UserInterface>({
    userDetails: null,
    isLoggedIn: false,
    idToken: null,
    base64IdToken: null,
    mobileOS: undefined,
    isStaffOrContractor: false,
    isOperator: false,
    isAdmin: false,
    sessionStart: null,
  });
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [logoutClicked, setLogoutClicked] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logUserOut, setLogUserOut] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState<boolean>(false);
  const [isSessionTimeout, setIsSessionTimeout] = useState<boolean>(false);
  const [userAvatarPath, setUserAvatarPath] = useState<string>();
  const {
    setActiveLocation,
    setLocationAssigned,
    setUserLocations,
    setLocationDetailsLoading,
  } = useLocationStore();

  const getStoredLocation = () => {
    const item = localStorage.getItem('location-storage');
    const parsedItem = item ? JSON.parse(item) : null;
    if (!parsedItem || parsedItem.state.activeLocation === '') {
      return null;
    }
    return parsedItem.state.activeLocation;
  };

  function checkAdminPermissions(perms: perms) {
    return !!perms?.CFAHELP_REACT?.ADMIN?.includes('00000');
  }

  //biome-ignore lint/correctness/useExhaustiveDependencies: maintain min dependencies
  useEffect(() => {
    if (authState?.isAuthenticated) {
      // Captured as consts so the narrowing below survives into the state
      // updater callback.
      const { idToken, accessToken } = authState;
      const accessClaims = accessToken ? getAccessTokenClaims(accessToken) : {};
      idToken &&
        accessToken &&
        setUserState(prev => ({
          userDetails: {
            display_name:
              accessClaims.nickname && accessClaims.nickname !== ''
                ? accessClaims.nickname
                : idToken.claims.firstName,
            display_last_name:
              accessClaims.family_name && accessClaims.family_name !== ''
                ? accessClaims.family_name
                : idToken.claims.lastName,
            ...idToken.claims,
          },
          isLoggedIn: true,
          idToken: idToken.idToken,
          base64IdToken: window.btoa(`{"Authorization":"${idToken.idToken}",
          "host":"${getEsrGraphqlHostForSubscription()}"
          }`),
          mobileOS: getMobileOS(),
          isOperator: idToken.claims.userType === 'Operator',
          isStaffOrContractor:
            idToken.claims.userType === 'Staff' ||
            idToken.claims.userType === 'Contractor',
          isAdmin: checkAdminPermissions(accessClaims.cfa_perms as perms),
          // Stamp once per sign-in. This effect reruns on every authState
          // change (including token renewals), and re-stamping made the
          // 'web logout' sessionLength far too short. Reset to null above.
          sessionStart: prev.sessionStart ?? Date.now(),
        }));

      //initializes amplitude
      const AMPLITUDE_TOKEN = getAmplitudeToken();
      //setting the defaultTracking to all false. we can change as needed down the road
      init(AMPLITUDE_TOKEN, authState?.idToken?.claims['cfa-guid'].toString(), {
        autocapture: {
          pageViews: false,
          formInteractions: false,
          fileDownloads: false,
          attribution: true,
        },
      });
    } else {
      return setUserState({
        userDetails: null,
        isLoggedIn: false,
        idToken: null,
        base64IdToken: null,
        mobileOS: undefined,
        isOperator: false,
        isStaffOrContractor: false,
        isAdmin: false,
        sessionStart: null,
      });
    }
  }, [authState, oktaAuth]);

  const lastNetworkInfoRef = useRef<CategoricalNetworkInfo | null>(null);
  useEffect(() => {
    if (!userState.isLoggedIn) return;
    const handleNetworkChange = () => {
      const current = getCategoricalNetworkInfo();
      if (
        lastNetworkInfoRef.current &&
        categoricalNetworkInfoEquals(lastNetworkInfoRef.current, current)
      ) {
        return;
      }
      lastNetworkInfoRef.current = current;
      setAmplitudeNetworkProps();
    };
    handleNetworkChange();
    const unsubscribe = subscribeToNetworkChange(handleNetworkChange);
    return () => {
      lastNetworkInfoRef.current = null;
      unsubscribe();
    };
  }, [userState.isLoggedIn]);

  //biome-ignore lint/correctness/useExhaustiveDependencies: maintain min dependencies
  useEffect(() => {
    if (!userState.isLoggedIn || !userState.userDetails) return;
    const storedLocation = getStoredLocation();

    setUserAvatar(
      userState.userDetails.cfa_aud,
      userState.userDetails['cfa-guid'],
      userState.userDetails.employeeNumber
    ).then(res => setUserAvatarPath(res));

    setAmplitudeUserProps(userState.userDetails.cfa_aud, { ...flags });

    if (userState.userDetails?.['cfa-guid']) {
      ldClient
        ?.identify({
          kind: 'multi',
          user: {
            key: userState.userDetails['cfa-guid'],
            name: userState.userDetails.name,
            version: __APP_VERSION__,
            audience: userState.userDetails.cfa_aud,
            locations: userState.userDetails?.cfa_perms.CFAHELP_REACT.LOGIN,
          },
          ld_application: {
            key: uuidv4(),
            name: 'Chick-fil-A Support',
            version: __APP_VERSION__,
            id: getViteAppUrl(),
          },
        })
        .then(() => {
          setLdIdentified(true);
        })
        .catch(error => {
          console.error('LaunchDarkly identify failed:', error);
          setLdIdentified(true);
        });
    }

    if (userState.userDetails?.cfa_perms.CFAHELP_REACT.LOGIN) {
      const restaurants =
        userState.userDetails.cfa_perms.CFAHELP_REACT.LOGIN.length > 1
          ? userState.userDetails.cfa_perms.CFAHELP_REACT.LOGIN.filter(
              (loc: string) => loc !== '00000'
            )
          : userState.userDetails.cfa_perms.CFAHELP_REACT.LOGIN;
      if (userState.idToken && userState?.isStaffOrContractor) {
        setActiveLocation(storedLocation ?? restaurants[0]);
        setLocationAssigned(true);
        return;
      }
      storedLocation && restaurants.includes(storedLocation)
        ? setActiveLocation(storedLocation)
        : setActiveLocation(restaurants[0]);
      if (userState.idToken) {
        setUserLocations(restaurants, userState.idToken);
        setLocationDetailsLoading(true);
      }
      if (
        userState.userDetails?.cfa_aud === 'OPERATOR_AUDIENCE' &&
        restaurants.length === 1 &&
        restaurants[0] === '00000'
      ) {
        return;
      }
      setLocationAssigned(true);
    }
  }, [userState.isLoggedIn]);

  return (
    <UserContext.Provider
      value={{
        userState,
        setUserState,
        isFormDirty,
        setIsFormDirty,
        setLogoutClicked,
        logoutClicked,
        setIsOnboarded,
        isOnboarded,
        isSessionTimeout,
        setIsSessionTimeout,
        userAvatarPath,
        setShowLogoutModal,
        showLogoutModal,
        setLogUserOut,
        logUserOut,
        ldIdentified,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
